import {
  afterNextRender,
  computed,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { UpdateUserDto, UserDto, UserService } from '@api-client';
import { BaseStore } from '@client';
import { CLIENT_ACCESS_TOKEN_KEY } from '@common';
import { lastValueFrom, map } from 'rxjs';

type State = {
  user: UserDto | null;
};

@Injectable({ providedIn: 'root' })
export class UserStore extends BaseStore<State> {
  private readonly userService = inject(UserService);
  user = computed(() => this.state()?.user);
  user$ = this.state$.pipe(map((state) => state.user));
  fetched = signal<boolean>(false);
  belongsToBootcamp = signal<boolean>(false);

  constructor() {
    super({ user: null });

    afterNextRender(() => {
      const accessToken = localStorage.getItem(CLIENT_ACCESS_TOKEN_KEY);
      if (!accessToken) {
        this.fetched.set(true);
        return;
      }

      this.fetchUser();
    });
  }

  setMember(user: UserDto | null) {
    this.updateState({ user });
  }

  async fetchUser() {
    const user = await lastValueFrom(this.userService.userControllerGetMe());
    this.fetched.set(true);
    if (!user) return null;

    this.setUser(user);

    this.checkBootcampBelonging();

    return user;
  }

  async checkBootcampBelonging() {
    const result = await lastValueFrom(
      this.userService.userControllerIsBelongToAnyBootcamp(),
    );
    this.belongsToBootcamp.set(result);
  }

  refresh = async () => {
    await this.checkBootcampBelonging();
  };

  async update(body: UpdateUserDto) {
    const user = await lastValueFrom(
      this.userService.userControllerUpdate({
        body,
      }),
    );

    this.setUser(user);
  }

  setUser(user: UserDto | null) {
    this.updateState({ user });
  }

  clearUser() {
    this.updateState({ user: null });
  }

  logout() {
    this.clearUser();
    localStorage.removeItem(CLIENT_ACCESS_TOKEN_KEY);
  }
}
